import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { US_STATES } from "@/lib/usStates";

// Pre-recording GEOGRAPHY gate. Runs after written consent and BEFORE the spoken
// ConsentGate trio (i.e. before any recording of any kind). The subject picks two
// US states — state of residence + state physically located at capture — which are
// POSTed to interview-geo-gate for a SERVER-AUTHORITATIVE eligibility decision
// (isEligibleState / _shared/eligibility.ts, TX-flag-aware). Eligible -> onAccepted
// (into the spoken trio). Excluded on either axis -> the edge fn terminates the
// interview (ineligible_geography) and returns eligible:false -> onIneligible (the
// parent routes to the terminal card). No mic, no MediaRecorder, no STT.
//
// Built off WrittenConsent's pattern: same statusOf helper + cbRef/mountedRef
// plumbing. Phase is "ready" | "submitting" (no mount-check like written consent;
// recoverable errors show inline on the form, hard/unexpected errors escalate to
// onError — the parent's generic error stage).
//
// The dropdown options (US_STATES) are DISPLAY-ONLY; eligibility stays server-side.
// Every option code is cross-checked to be recognized by the edge validator so a
// valid selection can never trigger a spurious 400.

type GeoGateResponse = {
  ok: boolean;
  eligible?: boolean;
  already_set?: boolean;
  already_terminated?: boolean;
  terminated_reason?: string;
  blocked_axes?: string[];
};

export type GeoGateProps = {
  resumeToken: string;
  onAccepted: () => void;
  onIneligible: () => void;
  onError: (message: string) => void;
  onSessionExpired: () => void;
};

// Pull the HTTP status off a supabase-js FunctionsHttpError (wrapped Response on
// error.context), matching the pattern used across the interview components.
const statusOf = (error: unknown): number | undefined => {
  const ctx = (error as { context?: Response }).context;
  return ctx && typeof ctx.status === "number" ? ctx.status : undefined;
};

type Phase = "ready" | "submitting";

const GeoGate = ({
  resumeToken,
  onAccepted,
  onIneligible,
  onError,
  onSessionExpired,
}: GeoGateProps) => {
  const [phase, setPhase] = useState<Phase>("ready");
  const [residence, setResidence] = useState("");
  const [captureLocation, setCaptureLocation] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Insulate the callbacks from parent re-renders — same ref pattern as
  // WrittenConsent / ConsentGate.
  const cbRef = useRef({ onAccepted, onIneligible, onError, onSessionExpired });
  cbRef.current = { onAccepted, onIneligible, onError, onSessionExpired };

  // Guard against setState/callbacks after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submit = async () => {
    // Both required; the button is disabled otherwise, but be defensive.
    if (!residence || !captureLocation) return;
    setPhase("submitting");
    setErrorMsg(null);
    const { data, error } = await supabase.functions.invoke<GeoGateResponse>(
      "interview-geo-gate",
      {
        headers: { Authorization: `Bearer ${resumeToken}` },
        body: { residence_state: residence, capture_location_state: captureLocation },
      },
    );
    if (!mountedRef.current) return;
    if (error) {
      const status = statusOf(error);
      if (status === 401 || status === 410) {
        cbRef.current.onSessionExpired();
        return;
      }
      if (status === 409) {
        // The pre-recording window moved under us (e.g. a concurrent advance).
        // Recoverable — surface inline and let them retry.
        setErrorMsg("Something changed while we were checking. Please try again.");
        setPhase("ready");
        return;
      }
      if (status === 400) {
        // Shouldn't happen — the dropdowns only offer codes the edge validator
        // recognizes. Recoverable inline (re-select + retry), never a loop.
        setErrorMsg("Please choose your U.S. state from each list and try again.");
        setPhase("ready");
        return;
      }
      // 500 / network / other -> generic error stage (parent).
      cbRef.current.onError("We couldn't check your location. Please try again in a few minutes.");
      return;
    }
    // 200 body branches.
    if (data?.ok && (data.eligible === true || data.already_set)) {
      cbRef.current.onAccepted();
      return;
    }
    if (data?.ok && data.eligible === false) {
      cbRef.current.onIneligible();
      return;
    }
    // Unexpected shape -> generic error stage (parent).
    cbRef.current.onError("We couldn't check your location. Please try again in a few minutes.");
  };

  const canSubmit = residence !== "" && captureLocation !== "" && phase !== "submitting";

  return (
    <div className="text-center" aria-live="polite">
      <div className="mb-6 text-sm uppercase tracking-wide text-muted-foreground">
        Before we begin
      </div>
      <h1 className="text-2xl font-display text-foreground">A quick location check</h1>
      <p className="mt-4 text-base text-muted-foreground max-w-md mx-auto">
        Before any recording begins, we need to confirm where you are. Please choose
        your U.S. state for each question below.
      </p>

      {errorMsg ? (
        <p className="mt-4 text-base text-destructive max-w-md mx-auto">{errorMsg}</p>
      ) : null}

      <div className="mt-6 max-w-md mx-auto text-left">
        <label className="block text-base text-foreground" htmlFor="geo-residence">
          What U.S. state do you live in?
        </label>
        <select
          id="geo-residence"
          value={residence}
          onChange={(e) => setResidence(e.target.value)}
          className="mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
        >
          <option value="">Select a state…</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 max-w-md mx-auto text-left">
        <label className="block text-base text-foreground" htmlFor="geo-location">
          What U.S. state are you in right now?
        </label>
        <select
          id="geo-location"
          value={captureLocation}
          onChange={(e) => setCaptureLocation(e.target.value)}
          className="mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
        >
          <option value="">Select a state…</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-8">
        <Button
          size="lg"
          onClick={() => void submit()}
          disabled={!canSubmit}
          className="text-xl px-10 py-6 h-auto rounded-2xl"
        >
          {phase === "submitting" ? "Checking…" : "Continue"}
        </Button>
      </div>
    </div>
  );
};

export default GeoGate;
